import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { invoiceStatusLabel } from "@/lib/finance";

const CURRENCIES = ["INR", "USD", "RMB"];

/** Phase 140 — single shared invoice creation/edit surface, used everywhere
 * an invoice gets created or amended: Home Dashboard, the Project page and
 * the Revenue Ledger. Replaces the old per-page inline forms so every
 * invoice goes through the exact same taxonomy dropdowns, HSN auto-fill,
 * currency-driven tax defaults and Draft -> Pending Approval -> Approved
 * workflow, no matter where it was created from. */
export default function GlobalInvoiceWizard({ project, invoice, trigger, onSaved, open: openProp, onOpenChange }) {
  const { user } = useAuth();
  const can = usePermissions();
  const isFounder = user?.role === "founder";
  const isFinance = can("finance");
  const isProjectCM = user?.role === "client_manager" && project?.client_manager_id === user?.id;
  const allowed = isFinance || isProjectCM;
  const editing = !!invoice;

  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = controlled ? onOpenChange : setOpenState;
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [revenueTree, setRevenueTree] = useState([]);
  const [addMainOpen, setAddMainOpen] = useState(false);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [newMainName, setNewMainName] = useState("");
  const [saving, setSaving] = useState(null); // "draft" | "submit" | "approve" | null

  const emptyForm = () => ({
    invoice_number: "",
    client_id: project?.client_user_id || "",
    client_name: project?.client_name || "",
    project_id: project?.id || "",
    amount: "",
    currency: "INR",
    date: new Date().toISOString().slice(0, 10),
    due_date: "",
    service_type: "",
    sub_service_type: "",
    hsn_code: "",
    service_description: "",
    gst: "",
    tds: "",
    drive_link: "",
    notes: "",
  });
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (!open) return;
    api.get("/admin/clients").then(({ data }) => setClients(data || [])).catch(() => setClients([]));
    api.get("/finance/revenue-categories").then(({ data }) => setRevenueTree(data?.categories || [])).catch(() => setRevenueTree([]));
    if (!project) api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
    setForm(editing ? {
      invoice_number: invoice.invoice_number || "",
      client_id: invoice.client_id || "",
      client_name: invoice.client_name || "",
      project_id: invoice.project_id || "",
      amount: invoice.amount ?? "",
      currency: invoice.currency || "INR",
      date: (invoice.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      due_date: (invoice.due_date || "").slice(0, 10) || "",
      service_type: invoice.service_type || "",
      sub_service_type: invoice.sub_service_type || "",
      hsn_code: invoice.hsn_code || "",
      service_description: invoice.service_description || "",
      gst: invoice.gst ?? "",
      tds: invoice.tds ?? "",
      drive_link: invoice.drive_link || "",
      notes: invoice.notes || "",
    } : emptyForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mains = useMemo(() => revenueTree.map((c) => c.main_category), [revenueTree]);
  const subTypes = useMemo(() => revenueTree.find((c) => c.main_category === form.service_type)?.sub_types || [], [revenueTree, form.service_type]);
  const isInr = (form.currency || "INR").toUpperCase() === "INR";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCurrencyChange = (v) => {
    setForm((f) => ({
      ...f,
      currency: v,
      gst: v === "INR" ? (Number(f.gst) > 0 ? f.gst : 18) : 0,
      tds: v === "INR" ? (Number(f.tds) > 0 ? f.tds : 10) : 0,
    }));
  };

  const onMainChange = (v) => {
    const cat = revenueTree.find((c) => c.main_category === v);
    setForm((f) => ({
      ...f, service_type: v, sub_service_type: "",
      hsn_code: cat?.hsn_code || f.hsn_code,
      service_description: cat?.default_description || f.service_description,
      gst: ((f.currency || "INR").toUpperCase() === "INR" && cat?.gst_rate != null) ? cat.gst_rate : f.gst,
    }));
  };

  const onClientChange = (id) => {
    const c = clients.find((cl) => cl.id === id);
    setForm((f) => ({ ...f, client_id: id, client_name: c?.name || c?.company_name || "" }));
  };

  const onProjectChange = (id) => {
    const p = projects.find((pr) => pr.id === id);
    setForm((f) => ({
      ...f, project_id: id,
      client_id: p?.client_user_id || f.client_id,
      client_name: p?.client_name || f.client_name,
    }));
  };

  const addMainCategory = async () => {
    if (!newMainName.trim()) return;
    try {
      const { data } = await api.post("/finance/revenue-categories/quick-add-main", { name: newMainName.trim() });
      setRevenueTree(data.categories || []);
      onMainChange(newMainName.trim());
      setNewMainName("");
      setAddMainOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addSubType = async () => {
    if (!newMainName.trim() || !form.service_type) return;
    try {
      const { data } = await api.post("/finance/revenue-categories/quick-add-sub", { main_category: form.service_type, sub_type: newMainName.trim() });
      setRevenueTree(data.categories || []);
      set("sub_service_type", newMainName.trim());
      setNewMainName("");
      setAddSubOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const validate = () => {
    if (!form.invoice_number.trim()) return "Invoice # is required";
    if (!form.client_id) return "Pick a Client";
    if (!form.amount || Number(form.amount) <= 0) return "Enter an amount";
    if (!form.service_type) return "Pick a Main Service Category";
    return null;
  };

  const buildPayload = () => ({
    ...form,
    amount: Number(form.amount) || 0,
    gst: form.gst === "" ? 0 : Number(form.gst),
    tds: form.tds === "" ? null : Number(form.tds),
  });

  const save = async (action) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (action === "submit" && !(form.drive_link || "").trim()) {
      toast.error("Attach a PDF or Google Drive link before submitting for approval");
      return;
    }
    setSaving(action);
    try {
      let iid = invoice?.id;
      const payload = buildPayload();
      if (editing) {
        await api.patch(`/finance/invoices/${iid}`, payload);
      } else if (project?.id) {
        const { data } = await api.post(`/projects/${project.id}/invoices`, payload);
        iid = data.id;
      } else {
        const { data } = await api.post("/finance/invoices", payload);
        iid = data.id;
      }
      if (action === "submit") await api.post(`/finance/invoices/${iid}/submit-for-approval`);
      if (action === "approve") await api.post(`/finance/invoices/${iid}/approve`);
      toast.success(
        action === "submit" ? "Submitted for Founder approval" :
        action === "approve" ? "Invoice approved — Ready for Zoho" :
        editing ? "Invoice updated" : "Invoice saved as Draft"
      );
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  if (!allowed && !editing) return null;

  const status = (invoice?.status || "draft").toLowerCase();
  const canSubmit = status === "draft";
  const canApprove = status === "pending_approval" && isFounder;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button size="sm" className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="global-invoice-wizard-trigger">
              <FileText size={13} className="mr-1.5" /> New Invoice
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="global-invoice-wizard">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {editing ? "Edit invoice" : "New invoice"}
            {editing && <span className="text-[10px] font-normal uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-sm" data-testid="global-invoice-wizard-status">{invoiceStatusLabel(status)}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="Invoice #">
            <Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="rounded-sm h-9" data-testid="giw-invoice-number" />
          </Field>
          <Field label="Client">
            <Select value={form.client_id} onValueChange={onClientChange}>
              <SelectTrigger className="rounded-sm h-9" data-testid="giw-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {!project ? (
            <Field label="Project (optional)">
              <Select value={form.project_id || "__none"} onValueChange={(v) => onProjectChange(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9" data-testid="giw-project-select"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No project</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          ) : (
            <Field label="Project"><div className="h-9 flex items-center text-sm text-slate-700 px-1" data-testid="giw-project-locked">{project.name}</div></Field>
          )}

          <Field label="Amount">
            <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="rounded-sm h-9" data-testid="giw-amount" />
          </Field>
          <Field label="Currency">
            <Select value={form.currency} onValueChange={onCurrencyChange}>
              <SelectTrigger className="rounded-sm h-9" data-testid="giw-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>

          <Field label="Invoice Date">
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="rounded-sm h-9" data-testid="giw-date" />
          </Field>
          <Field label="Due Date">
            <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="rounded-sm h-9" data-testid="giw-due-date" />
          </Field>

          <Field label="Main Service Category">
            <div className="flex gap-1.5">
              <Select value={form.service_type} onValueChange={(v) => (v === "__add" ? setAddMainOpen(true) : onMainChange(v))}>
                <SelectTrigger className="rounded-sm h-9" data-testid="giw-service-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {mains.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  {isFinance && <SelectItem value="__add"><span className="text-[#F54800] inline-flex items-center gap-1"><Plus size={11} /> Add new</span></SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </Field>
          <Field label="Sub-Service Type (optional)">
            <Select value={form.sub_service_type || "__none"} onValueChange={(v) => (v === "__add" ? setAddSubOpen(true) : set("sub_service_type", v === "__none" ? "" : v))}>
              <SelectTrigger className="rounded-sm h-9" disabled={!form.service_type} data-testid="giw-sub-service-type"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {subTypes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                {isFinance && form.service_type && <SelectItem value="__add"><span className="text-[#F54800] inline-flex items-center gap-1"><Plus size={11} /> Add new</span></SelectItem>}
              </SelectContent>
            </Select>
          </Field>

          <Field label="HSN Code">
            <Input value={form.hsn_code} onChange={(e) => set("hsn_code", e.target.value)} placeholder="Auto-filled from category" className="rounded-sm h-9" data-testid="giw-hsn-code" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={isInr ? "GST %" : "GST % (LUT Exempt)"}>
              <Input type="number" value={form.gst} onChange={(e) => set("gst", e.target.value)} disabled={!isInr} className="rounded-sm h-9" data-testid="giw-gst" />
            </Field>
            <Field label={isInr ? "TDS %" : "TDS % (LUT Exempt)"}>
              <Input type="number" value={form.tds} onChange={(e) => set("tds", e.target.value)} disabled={!isInr} className="rounded-sm h-9" data-testid="giw-tds" />
            </Field>
          </div>

          <div className="col-span-2">
            <Field label="Service Description (on PDF)">
              <Textarea rows={2} value={form.service_description} onChange={(e) => set("service_description", e.target.value)} placeholder="Auto-filled from the Main Service Category" className="rounded-sm text-sm" data-testid="giw-service-description" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="PDF / Google Drive Link">
              <Input value={form.drive_link} onChange={(e) => set("drive_link", e.target.value)} placeholder="https://drive.google.com/…" className="rounded-sm h-9" data-testid="giw-drive-link" />
              <p className="text-[10px] text-slate-400 mt-1">Required before this invoice can be submitted for Founder approval.</p>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Notes (optional)">
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm text-sm" data-testid="giw-notes" />
            </Field>
          </div>
        </div>

        {addMainOpen && (
          <QuickAddInline label="New Main Service Category" value={newMainName} onChange={setNewMainName} onAdd={addMainCategory} onCancel={() => { setAddMainOpen(false); setNewMainName(""); }} testId="giw-add-main" />
        )}
        {addSubOpen && (
          <QuickAddInline label={`New Sub-Service Type under "${form.service_type}"`} value={newMainName} onChange={setNewMainName} onAdd={addSubType} onCancel={() => { setAddSubOpen(false); setNewMainName(""); }} testId="giw-add-sub" />
        )}

        <DialogFooter className="flex-wrap gap-2">
          {canApprove && (
            <Button onClick={() => save("approve")} disabled={!!saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="giw-approve-btn">
              <ShieldCheck size={13} className="mr-1.5" /> {saving === "approve" ? "Approving…" : "Approve"}
            </Button>
          )}
          {(!editing || canSubmit) && (
            <Button onClick={() => save("submit")} disabled={!!saving} variant="outline" className="rounded-sm" data-testid="giw-submit-approval-btn">
              {saving === "submit" ? "Submitting…" : "Save & Submit for Approval"}
            </Button>
          )}
          <Button onClick={() => save("draft")} disabled={!!saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="giw-save-draft-btn">
            {saving === "draft" ? "Saving…" : editing ? "Save changes" : "Save as Draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function QuickAddInline({ label, value, onChange, onAdd, onCancel, testId }) {
  return (
    <div className="border border-dashed border-[#F54800]/40 rounded-sm p-3 bg-orange-50/40 mb-2" data-testid={testId}>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-8 text-sm" autoFocus data-testid={`${testId}-input`} />
        <Button size="sm" onClick={onAdd} className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid={`${testId}-confirm`}>Add</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} className="rounded-sm h-8" data-testid={`${testId}-cancel`}>Cancel</Button>
      </div>
    </div>
  );
}
